import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { clientReferencesApi } from '../../services/webApi';

const Referanslar = () => {
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    // Initialize AOS
    const initAOS = () => {
      if (window.AOS) {
        window.AOS.init();
      }
    };

    // Initialize Lucide Icons
    const initLucide = () => {
      if (window.lucide && window.lucide.createIcons) {
        window.lucide.createIcons();
      }
    };

    // Fetch references from API
    const fetchReferences = async () => {
      try {
        const response = await clientReferencesApi.getAll();
        if (isMounted && response.data.success) {
          setReferences(response.data.data);
        } else if (isMounted) {
          setError('Veriler yüklenemedi');
        }
      } catch (err) {
        console.error('References fetch error:', err);
        if (isMounted) {
          setError('Veriler yüklenirken hata oluştu');
          // Fallback to static data
          setReferences([
            { company_name: 'MerkürNet', logo: '/web-theme/images/logo/merkurnet.png' },
            { company_name: 'İnternet Kutusu', logo: '/web-theme/images/logo/internet-kutusu.png' },
            { company_name: 'TrendNET', logo: '/web-theme/images/logo/trendnet.png' },
            { company_name: 'Daha Online', logo: '/web-theme/images/logo/daha-online.webp' },
            { company_name: 'MaviNet', logo: '/web-theme/images/logo/mavinet.png' },
            { company_name: 'DijitalNet', logo: '/web-theme/images/logo/dijitalnet.png' },
            { company_name: 'ArasNet', logo: '/web-theme/images/logo/arasnet.png' },
            { company_name: 'GöktürkNet', logo: '/web-theme/images/logo/gokturknet.png' },
            { company_name: 'İnciNet', logo: '/web-theme/images/logo/incinet.svg' }
          ]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // Hemen başlat
    initAOS();
    fetchReferences();

    return () => {
      isMounted = false;
    };
  }, []);

  // Initialize icons when references are loaded and periodically refresh
  useEffect(() => {
    const initIcons = () => {
      if (window.lucide && window.lucide.createIcons) {
        try {
          window.lucide.createIcons();
        } catch (error) {
          console.error('Error creating icons:', error);
        }
      }
    };

    if (!loading) {
      // Initial setup
      setTimeout(initIcons, 100);
      
      // Also re-init periodically to catch any missed icons
      const interval = setInterval(initIcons, 500);
      
      return () => clearInterval(interval);
    }
  }, [loading, references.length]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-white">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <>
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-[72px] backdrop-blur-3xl">
        <div className="overlay"></div>
        <div className="stars" aria-hidden="true"></div>
        <div className="starts2" aria-hidden="true"></div>
        <div className="stars3" aria-hidden="true"></div>

        <div className="px-6 pt-20 overflow-hidden" data-aos="fade-up" data-aos-easing="ease" data-aos-duration="800">
          <div className="container">
            <div className="py-14 text-center relative">
              <div className="flex justify-center">
                <div className="max-w-2xl">
                  <h2 className="md:text-6xl/tight text-5xl text-default-100 font-semibold mb-6">
                    <span className="bg-gradient-to-l from-rose-500 to-primary text-transparent bg-clip-text">Referanslar</span>
                    <br />Mutlu Müşterilerimiz
                  </h2>
                  <p className="text-sm font-medium text-default-400 mt-5">
                    Türkiye'nin dört bir yanından müşterilerimiz ISP Radius ile işlerini büyütüyor.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* References Grid */}
      <section className="py-14">
        <div className="container">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {references.map((reference, index) => (
              <div 
                key={index}
                className="group p-4 rounded-xl bg-default-950/40 transition-all duration-500 hover:-translate-y-2 hover:bg-primary/40"
                data-aos="fade-up" 
                data-aos-easing="ease" 
                data-aos-duration="1000"
                data-aos-delay={index * 100}
              >
                {/* Logo - Ortalı */}
                <div className="text-center">
                  <div className="w-32 h-20 mx-auto mb-4 bg-white rounded-lg flex items-center justify-center px-3 py-3" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img 
                      src={reference.logo} 
                      alt={reference.company_name} 
                      className="max-w-full max-h-full object-contain"
                        style={{ 
                          objectFit: 'contain',
                          maxWidth: '100%',
                          maxHeight: '100%',
                          width: 'auto',
                          height: 'auto',
                          display: 'block',
                          margin: '0 auto'
                        }}
                    />
                  </div>
                  <h3 className="text-xl font-medium text-white">{reference.company_name}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14">
        <div className="container">
          <div className="bg-default-950/40 backdrop-blur-3xl rounded-xl p-8 text-center" data-aos="zoom-in" data-aos-easing="ease" data-aos-duration="1000">
            <div className="inline-flex items-center justify-center h-16 w-16 bg-primary/10 text-primary rounded-xl mb-10 mx-auto">
              <i data-lucide="users" className="h-10 w-10"></i>
            </div>
            <h2 className="text-2xl text-white font-medium mb-4">Siz de Başarı Hikayelerimizden Biri Olun</h2>
            <p className="text-base text-default-200 mb-6 max-w-2xl mx-auto">
              ISP Radius ile işletmenizi büyütün ve müşteri memnuniyetinizi artırın. Ücretsiz demo talebinde bulunun.
            </p>
            <Link to="/iletisim" className="inline-flex items-center justify-center gap-2 bg-primary text-white py-2 px-6 rounded-full hover:bg-primary-hover transition-all duration-300">
              Ücretsiz Demo Talebi
              <i data-lucide="arrow-right" className="h-5 w-5"></i>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Referanslar;